import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import { 
  fetchMySubAccounts, 
  changeSubAccountPassword, 
  deleteSubAccount
} from '../app/redux/slices/masterSubAccountSlice';
import { selectIsAuthenticated } from '../app/redux/slices/authSlice';
import { getScrollInfoV2 } from '../app/redux/slices/dataSlice';
import Navigation from '../app/components/NavigationSPA';
import { useTranslation } from 'react-i18next';
import { logger } from '../app/utils/logger';

export default function SubAccountDetailsPage() {
  const { t } = useTranslation(['teams', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id: subAccountId } = useParams<{ id: string }>();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { isMasterAccount, masterAccountInfo, loading } = useSelector(
    (state: RootState) => state.masterSubAccount
  );

  const [mounted, setMounted] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [probesCount, setProbesCount] = useState<number | null>(null);
  const [loadingProbes, setLoadingProbes] = useState(false);

  // Find the sub-account from Redux state
  const subAccount = masterAccountInfo?.subAccounts.find(
    (sub) => sub.uid === subAccountId
  );
  
  // Get team name
  const teamName: string | undefined = subAccount && Array.isArray(subAccount.teamName)
    ? subAccount.teamName[0]
    : (typeof subAccount?.teamName === 'string' ? subAccount.teamName : undefined);


  // Handle hydration
  useEffect(() => {
    setMounted(true);
  }, []);

  // Check authentication
  useEffect(() => {
    if (mounted && !isAuthenticated) {
      navigate('/login');
    }
  }, [mounted, isAuthenticated, navigate]);

  // Check if user is master account
  useEffect(() => {
    if (mounted && !isMasterAccount) {
      navigate('/dashboard');
    }
  }, [mounted, isMasterAccount, navigate]);

  // Check if ID is provided
  useEffect(() => {
    if (mounted && !subAccountId) {
      navigate('/teams');
    }
  }, [mounted, subAccountId, navigate]);

  // Fetch sub-accounts if not loaded
  useEffect(() => {
    if (mounted && isAuthenticated && isMasterAccount && !subAccount && subAccountId) {
      dispatch(fetchMySubAccounts());
    }
  }, [mounted, isAuthenticated, isMasterAccount, subAccount, subAccountId, dispatch]);

  // Fetch team probes count when sub-account is loaded (only once)
  useEffect(() => {
    const fetchProbesCount = async () => {
      if (mounted && teamName && probesCount === null) {
        setLoadingProbes(true);
        try {
          const result = await dispatch(getScrollInfoV2({
            teamName: teamName,
            page: 1,
            pageSize: 1, // Only need count, not actual data
            filter: 'id',
            ascend: true
          })).unwrap();
          
          // Use trapLength for the total count, not traps.length
          const count = result.trapLength || 0;
          setProbesCount(count);
          logger.log(`✅ SmartProbes count for ${teamName}:`, count);
        } catch (error) {
          logger.error('❌ Failed to fetch SmartProbes count:', error);
          setProbesCount(0);
        } finally {
          setLoadingProbes(false);
        }
      }
    };
    
    fetchProbesCount();
  }, [mounted, teamName, probesCount, dispatch]);

  // Password validation
  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setPasswordError(t('common:passwordMinLength'));
      return false;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(password)) {
      setPasswordError(t('common:passwordRequirements'));
      return false;
    }

    setPasswordError('');
    return true;
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!subAccountId) return;
    
    if (!validatePassword(newPassword)) {
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError(t('common:passwordsDoNotMatch'));
      return;
    }

    try {
      await dispatch(
        changeSubAccountPassword({
          targetSubAccountUID: subAccountId,
          newPassword: newPassword,
        })
      ).unwrap();

      alert(t('common:success'));
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error: any) {
      alert(error.message || t('common:error'));
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (!subAccountId) return;
    
    try {
      await dispatch(deleteSubAccount(subAccountId)).unwrap();
      alert(t('common:success'));
      navigate('/teams');
    } catch (error: any) {
      alert(error.message || t('common:error'));
    }
  };

  // Don't render until mounted
  if (!mounted) {
    return null;
  }

  // Loading state
  if (loading.subAccounts || !subAccount) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Back Button */}
          <button
            onClick={() => navigate('/teams')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('backToSubTeams')}
          </button>

          <div className="space-y-8">
            {/* Account Information Section */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">{t('accountInformation')}</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between py-2">
                    <span className="text-sm font-medium text-gray-800">{t('company')}</span>
                    <span className="text-sm text-gray-900">{subAccount.company || 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-sm font-medium text-gray-800">{t('accountName')}</span>
                    <span className="text-sm text-gray-900">{subAccount.username || 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-sm font-medium text-gray-800">{t('status')}</span>
                    <span className={`text-sm ${subAccount.deactivated ? 'text-red-600' : 'text-green-600'}`}>
                      {subAccount.deactivated ? t('inactive') : t('active')}
                    </span>
                  </div>
                  
                  <div className="flex justify-between py-2">
                    <span className="text-sm font-medium text-gray-800">{t('created')}</span>
                    <span className="text-sm text-gray-900">
                      {subAccount.createdAt ? new Date(subAccount.createdAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* SmartProbe Management Section */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">{t('smartProbeManagement')}</h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between py-2">
                    <span className="text-sm font-medium text-gray-800">{t('totalSmartProbes')}</span>
                    <span className="text-sm text-gray-900">
                      {loadingProbes ? (
                        <span className="text-gray-400">{t('loading')}</span>
                      ) : (
                        probesCount !== null ? probesCount : 0
                      )}
                    </span>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/move-smartprobe?sourceTeamId=${subAccount.uid}`)}
                      className="w-full flex items-center justify-between py-3 px-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors cursor-pointer"
                    >
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium text-gray-900">{t('moveSmartProbe')}</p>
                        <p className="text-xs text-gray-800 mt-1">{t('transferSmartProbes')}</p>
                      </div>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Account Management Section */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">{t('accountManagement')}</h2>
              </div>
              <div className="p-6">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="w-full flex items-center justify-between py-3 px-4 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors cursor-pointer"
                >
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900">{t('changePassword')}</p>
                    <p className="text-xs text-gray-800 mt-1">{t('setNewPassword')}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Danger Zone Section */}
            <div className="bg-white shadow rounded-lg border-2 border-red-200">
              <div className="px-6 py-4 border-b border-red-200 bg-red-50">
                <h2 className="text-lg font-medium text-red-900">{t('dangerZone')}</h2>
              </div>
              <div className="p-6">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center justify-between py-3 px-4 border border-red-300 rounded-lg hover:bg-red-100 hover:border-red-400 transition-colors cursor-pointer"
                >
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-red-700">{t('deleteSubAccount')}</p>
                    <p className="text-xs text-red-600 mt-1">{t('permanentlyDelete')}</p>
                  </div>
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('changePassword')}</h2>
            
            {/* New Password */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('newPasswordFor')} {subAccount.company}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('enterNewPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-900"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-800 mt-2">
                {t('passwordMustBe')}
              </p>
            </div>

            {/* Confirm Password */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('confirmNewPassword')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('reEnterNewPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-900"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && (
                <p className="text-red-500 text-sm mt-1">{passwordError}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleChangePassword}
                disabled={loading.changePassword}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
              >
                {loading.changePassword ? t('changing') : t('changePassword')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('deleteSubAccount')}</h2>
            <p className="text-gray-900 mb-6">
              {t('deleteConfirmMessage')} <strong>{subAccount.company}</strong>? {t('cannotBeUndone')}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading.deleteSubAccount}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors"
              >
                {loading.deleteSubAccount ? t('deleting') : t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
